import {useMemo} from "react";
import moment from "moment";
import {
	Area,
	CartesianGrid,
	ComposedChart,
	Line,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

import {COLORS} from "@/style/utils";

import "./index.scss";

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (v, d = 1) => {
	if (v == null || isNaN(v)) return "—";
	return Number(v).toFixed(d);
};

const computeAge = (birthDate, atDate) => {
	if (!birthDate) return null;
	return moment(atDate).diff(moment(birthDate), "years", true);
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function PlayerDevelopmentProfile({playerData, coortePlayers}) {
	const birthDate = playerData?.dateOfBirth || playerData?.birthDate;
	const birthYear = birthDate ? moment(birthDate).year() : null;

	// ── Growth data (sorted by date) ──────────────────────────────────────────

	const heightData = useMemo(() => {
		return (playerData?.playersGrowth || [])
			.filter((g) => g.height && g.date)
			.map((g) => ({
				date: g.date,
				label: moment(g.date).format("MMM YY"),
				age: computeAge(birthDate, g.date),
				value: parseFloat(g.height),
			}))
			.sort((a, b) => new Date(a.date) - new Date(b.date));
	}, [playerData?.playersGrowth, birthDate]);

	const weightData = useMemo(() => {
		return (playerData?.playersGrowth || [])
			.filter((g) => g.weight && g.date)
			.map((g) => ({
				date: g.date,
				label: moment(g.date).format("MMM YY"),
				age: computeAge(birthDate, g.date),
				value: parseFloat(g.weight),
			}))
			.sort((a, b) => new Date(a.date) - new Date(b.date));
	}, [playerData?.playersGrowth, birthDate]);

	// ── Coorte comparison ─────────────────────────────────────────────────────

	const coorteStats = useMemo(() => {
		if (!birthYear || !coortePlayers?.length) return null;

		const sameBirthYear = coortePlayers.filter((p) => {
			const pYear = p.dateOfBirth ? moment(p.dateOfBirth).year() : null;
			return pYear === birthYear && p.id !== playerData?.id;
		});

		if (!sameBirthYear.length) return null;

		const heights = sameBirthYear
			.map((p) => parseFloat(p.height))
			.filter((v) => v && !isNaN(v));
		const weights = sameBirthYear
			.map((p) => parseFloat(p.weight))
			.filter((v) => v && !isNaN(v));

		const avg = (arr) =>
			arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : null;
		const max = (arr) => (arr.length ? Math.max(...arr) : null);
		const min = (arr) => (arr.length ? Math.min(...arr) : null);

		return {
			count: sameBirthYear.length,
			avgHeight: avg(heights),
			minHeight: min(heights),
			maxHeight: max(heights),
			avgWeight: avg(weights),
			minWeight: min(weights),
			maxWeight: max(weights),
		};
	}, [birthYear, coortePlayers, playerData?.id]);

	// ── Growth velocity (cm/anno) ─────────────────────────────────────────────

	const growthVelocity = useMemo(() => {
		if (heightData.length < 2) return null;
		const first = heightData[0];
		const last = heightData[heightData.length - 1];
		const yearsDiff = moment(last.date).diff(moment(first.date), "years", true);
		if (yearsDiff < 0.25) return null;
		return (last.value - first.value) / yearsDiff;
	}, [heightData]);

	// ── Development indicators ────────────────────────────────────────────────

	const currentHeight = playerData?.height ? parseFloat(playerData.height) : null;
	const currentWeight = playerData?.weight ? parseFloat(playerData.weight) : null;

	const heightDelta = useMemo(() => {
		if (!currentHeight || !coorteStats?.avgHeight) return null;
		return currentHeight - coorteStats.avgHeight;
	}, [currentHeight, coorteStats]);

	const weightDelta = useMemo(() => {
		if (!currentWeight || !coorteStats?.avgWeight) return null;
		return currentWeight - coorteStats.avgWeight;
	}, [currentWeight, coorteStats]);

	// ── Chart data with coorte band ───────────────────────────────────────────

	const heightChartData = useMemo(() => {
		if (!heightData.length) return [];
		return heightData.map((d) => ({
			...d,
			coorteAvg: coorteStats?.avgHeight ?? null,
			coorteMin: coorteStats?.minHeight ?? null,
			coorteMax: coorteStats?.maxHeight ?? null,
		}));
	}, [heightData, coorteStats]);

	const weightChartData = useMemo(() => {
		if (!weightData.length) return [];
		return weightData.map((d) => ({
			...d,
			coorteAvg: coorteStats?.avgWeight ?? null,
			coorteMin: coorteStats?.minWeight ?? null,
			coorteMax: coorteStats?.maxWeight ?? null,
		}));
	}, [weightData, coorteStats]);

	// ── No data guard ─────────────────────────────────────────────────────────

	if (!heightData.length && !weightData.length && !coorteStats) return null;

	// ── Render ────────────────────────────────────────────────────────────────

	const CHART = {
		primary: COLORS.primary,
		coorte: COLORS.textMuted,
		coorteBand: "var(--color-surface-soft)",
	};

	const renderChart = (data, label, unit) => {
		if (!data.length) return null;
		const hasCoorte = data[0]?.coorteMin != null;
		return (
			<div className="pdp-chart">
				<div className="pdp-chart__title">{label}</div>
				<ResponsiveContainer width="100%" height={180}>
					<ComposedChart data={data} margin={{top: 5, right: 10, left: -15, bottom: 0}}>
						<CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
						<XAxis
							dataKey="label"
							tick={{fontSize: 10, fill: COLORS.textMuted}}
							axisLine={false}
							tickLine={false}
						/>
						<YAxis
							tick={{fontSize: 10, fill: COLORS.textMuted}}
							axisLine={false}
							tickLine={false}
							domain={["dataMin - 2", "dataMax + 2"]}
							unit={unit}
						/>
						<Tooltip
							contentStyle={{
								fontSize: 12,
								borderRadius: 8,
								border: "1px solid " + COLORS.border,
								boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
							}}
							formatter={(v, name) => {
								const labels = {
									value: label,
									coorteAvg: "Media coorte",
								};
								return [v != null ? `${Number(v).toFixed(1)}${unit}` : "—", labels[name] || name];
							}}
						/>
						{hasCoorte && (
							<Area
								dataKey="coorteMax"
								stackId="band"
								fill={CHART.coorteBand}
								stroke="none"
								baseLine={data.map((d) => d.coorteMin)}
								type="monotone"
							/>
						)}
						{hasCoorte && (
							<Line
								dataKey="coorteAvg"
								stroke={CHART.coorte}
								strokeWidth={1.5}
								strokeDasharray="6 3"
								dot={false}
								name="Media coorte"
							/>
						)}
						<Line
							dataKey="value"
							stroke={CHART.primary}
							strokeWidth={2.5}
							dot={{r: 3, fill: CHART.primary}}
							activeDot={{r: 5}}
							name={label}
						/>
					</ComposedChart>
				</ResponsiveContainer>
			</div>
		);
	};

	return (
		<div className="pdp-root">
			<div className="pdp-header">
				<span className="pdp-header__title">Profilo di Sviluppo</span>
				{birthYear && (
					<span className="pdp-header__meta">Classe {birthYear}</span>
				)}
			</div>

			{/* ── Indicators ──────────────────────────────────────────────── */}
			<div className="pdp-indicators">
				<div className="pdp-indicator">
					<span className="pdp-indicator__value">{fmt(currentHeight, 0)}</span>
					<span className="pdp-indicator__unit">cm</span>
					<span className="pdp-indicator__label">Altezza</span>
					{heightDelta != null && (
						<span className={`pdp-indicator__delta pdp-indicator__delta--${heightDelta >= 0 ? "up" : "down"}`}>
							{heightDelta >= 0 ? "+" : ""}{fmt(heightDelta, 1)} vs coorte
						</span>
					)}
				</div>
				<div className="pdp-indicator">
					<span className="pdp-indicator__value">{fmt(currentWeight, 0)}</span>
					<span className="pdp-indicator__unit">kg</span>
					<span className="pdp-indicator__label">Peso</span>
					{weightDelta != null && (
						<span className={`pdp-indicator__delta pdp-indicator__delta--${weightDelta >= 0 ? "up" : "down"}`}>
							{weightDelta >= 0 ? "+" : ""}{fmt(weightDelta, 1)} vs coorte
						</span>
					)}
				</div>
				{growthVelocity != null && (
					<div className="pdp-indicator">
						<span className="pdp-indicator__value">{fmt(growthVelocity, 1)}</span>
						<span className="pdp-indicator__unit">cm/anno</span>
						<span className="pdp-indicator__label">Velocita crescita</span>
					</div>
				)}
				{coorteStats && (
					<div className="pdp-indicator pdp-indicator--coorte">
						<span className="pdp-indicator__value">{coorteStats.count}</span>
						<span className="pdp-indicator__unit">giocatori</span>
						<span className="pdp-indicator__label">Coorte {birthYear}</span>
						<span className="pdp-indicator__delta">
							media {fmt(coorteStats.avgHeight, 0)}cm / {fmt(coorteStats.avgWeight, 0)}kg
						</span>
					</div>
				)}
			</div>

			{/* ── Charts ──────────────────────────────────────────────────── */}
			<div className="pdp-charts">
				{renderChart(heightChartData, "Altezza", "cm")}
				{renderChart(weightChartData, "Peso", "kg")}
			</div>
		</div>
	);
}
